import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../config/axios';
import { MdAssignment, MdSave, MdCancel, MdCheck, MdChevronRight, MdChevronLeft, MdDateRange } from 'react-icons/md';
import { TbEdit } from "react-icons/tb";
import { useLanguage } from '../context/LanguageContext';

const TimesheetPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedWeek, setSelectedWeek] = useState(() => {
    // Bugünün bulunduğu haftanın başlangıcını hesapla
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Pazartesi
    
    // Yerel saat kullanarak tarih formatı
    const year = startOfWeek.getFullYear();
    const month = String(startOfWeek.getMonth() + 1).padStart(2, '0');
    const day = String(startOfWeek.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [timesheetData, setTimesheetData] = useState({});
  const [loading, setLoading] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [editData, setEditData] = useState({ detail: '', workhour: '' });
  const [message, setMessage] = useState({ type: '', text: '' });

  // Seçili haftanın günlerini oluştur
  const getWeekDays = (weekStart) => {
    if (!weekStart) return [];
    
    try {
      const startDate = new Date(weekStart);
      
      // Geçersiz tarih kontrolü
      if (isNaN(startDate.getTime())) {
        console.error('Geçersiz hafta tarihi:', weekStart);
        return [];
      }
      
      const days = [];
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        
        // Yerel saat kullanarak tarih formatı
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        days.push({
          date: dateStr,
          dayName: date.toLocaleDateString('tr-TR', { weekday: 'long' }),
          dayShort: date.toLocaleDateString('tr-TR', { weekday: 'short' }),
          dayNumber: date.getDate(),
          month: date.toLocaleDateString('tr-TR', { month: 'short' })
        });
      }
      
      return days;
    } catch (error) {
      console.error('Hafta günleri oluşturulurken hata:', error);
      return [];
    }
  };

  const weekDays = getWeekDays(selectedWeek);
  

  // Müşterileri getir
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get('/customers', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomers(response.data);
      } catch (error) {
        console.error('Müşteriler yüklenirken hata:', error);
      }
    };

    fetchCustomers();
  }, []);

  // Seçili müşteri ve hafta için timesheet verilerini getir
  useEffect(() => {
    if (selectedCustomer && selectedWeek) {
      fetchTimesheetData();
    }
  }, [selectedCustomer, selectedWeek]);

    const fetchTimesheetData = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await api.get(`/timesheet/${selectedCustomer}/${selectedWeek}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Verileri günlere göre organize et
      const organizedData = {};
      
      response.data.forEach(item => {
        // Backend'den gelen tarihi doğru şekilde parse et
        let date;
        
        if (item.timesheet_date.includes('T')) {
          // ISO format: "2025-08-18T00:00:00.000Z" - UTC'den yerel tarihe çevir
          const utcDate = new Date(item.timesheet_date);
          // Yerel tarih formatını kullan (YYYY-MM-DD)
          const year = utcDate.getFullYear();
          const month = String(utcDate.getMonth() + 1).padStart(2, '0');
          const day = String(utcDate.getDate()).padStart(2, '0');
          date = `${year}-${month}-${day}`;
        } else {
          // MySQL format: "2025-08-18 00:00:00" - zaten yerel format
          date = item.timesheet_date.split(' ')[0];
        }
        
        organizedData[date] = {
          id: item.id,
          detail: item.timesheet_detail,
          workhour: item.timesheet_workhour,
          date: date
        };
      });
      
      setTimesheetData(organizedData);
    } catch (error) {
      console.error('Timesheet verileri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };



  const handleEdit = (day) => {
    const existingData = timesheetData[day.date] || {};
    setEditData({
      detail: existingData.detail || '',
      workhour: existingData.workhour || ''
    });
    setEditingDay(day.date);
  };

  const handleSave = async (day) => {
    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user'));
      
      const data = {
        timesheet_detail: editData.detail,
        timesheet_workhour: parseFloat(editData.workhour) || 0,
        timesheet_date: day.date,
        timesheet_customer: parseInt(selectedCustomer),
        created_by: userData.id
      };

      if (timesheetData[day.date]?.id) {
        // Güncelle
        await api.put(`/timesheet/${timesheetData[day.date].id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Yeni kayıt
        await api.post('/timesheet', data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setEditingDay(null);
      fetchTimesheetData();
    } catch (error) {
      setMessage({ type: 'error', text: t('timesheet.saveError') });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleCancel = () => {
    setEditingDay(null);
    setEditData({ detail: '', workhour: '' });
  };

  const formatWorkHour = (workhour) => {
    if (!workhour || workhour === 0) return '';
    return `${workhour} ${t('timesheet.hours')}`;
  };

  // Hafta navigasyon fonksiyonları
  const goToPreviousWeek = () => {
    if (!selectedWeek) return;
    const currentDate = new Date(selectedWeek);
    const previousWeek = new Date(currentDate);
    previousWeek.setDate(currentDate.getDate() - 7);
    
    const year = previousWeek.getFullYear();
    const month = String(previousWeek.getMonth() + 1).padStart(2, '0');
    const day = String(previousWeek.getDate()).padStart(2, '0');
    setSelectedWeek(`${year}-${month}-${day}`);
  };

  const goToNextWeek = () => {
    if (!selectedWeek) return;
    const currentDate = new Date(selectedWeek);
    const nextWeek = new Date(currentDate);
    nextWeek.setDate(currentDate.getDate() + 7);
    
    const year = nextWeek.getFullYear();
    const month = String(nextWeek.getMonth() + 1).padStart(2, '0');
    const day = String(nextWeek.getDate()).padStart(2, '0');
    setSelectedWeek(`${year}-${month}-${day}`);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Pazartesi
    
    const year = startOfWeek.getFullYear();
    const month = String(startOfWeek.getMonth() + 1).padStart(2, '0');
    const day = String(startOfWeek.getDate()).padStart(2, '0');
    setSelectedWeek(`${year}-${month}-${day}`);
  };

  // Hafta formatını göstermek için
  const getWeekDisplayText = (weekStart) => {
    if (!weekStart) return '';
    const startDate = new Date(weekStart);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    
    const startStr = startDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    const endStr = endDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    return `${startStr} - ${endStr}`;
  };




  return (
    <div className="h-full overflow-auto">
      {/* Breadcrumb */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <nav className="flex items-center text-xs sm:text-sm">
          <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
            <span className="text-[#fff] font-medium">
              {t('timesheet.title')}
            </span>
            <MdChevronRight className="mx-2 text-[#fff]" size={20} />
            <span className="text-[#fff] font-medium">
              {t('common.timesheetEntry')}
            </span>
          </div>
        </nav>
      </div>

      <div className="space-y-6">

      {/* Mesaj */}
      {message.text && (
        <div className={`p-4 rounded-md ${
          message.type === 'error' ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400' : 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' && <MdCheck size={18} />}
            <span>{message.text}</span>
          </div>
        </div>
      )}

             {/* Müşteri ve Hafta Seçimi */}
       <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
         <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
           <h2 className="text-lg font-medium text-gray-800 dark:text-white">
             {t('timesheet.customerAndWeekSelection')}
           </h2>
         </div>
         <div className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('timesheet.customer')}
            </label>
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">{t('timesheet.selectCustomer')}</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.customer_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('timesheet.weekSelection')}
            </label>
            
            {/* Hafta seçici - compact tasarım */}
            <div className="flex items-center justify-between border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md px-3 py-2">
              <button
                type="button"
                onClick={goToPreviousWeek}
                className="flex items-center justify-center w-6 h-6 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                title="Önceki Hafta"
              >
                <MdChevronLeft size={16} />
              </button>
              
              <div className="flex-1 text-center">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {getWeekDisplayText(selectedWeek)}
                </div>
                <button
                  type="button"
                  onClick={goToCurrentWeek}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  {t('timesheet.currentWeek')}
                </button>
              </div>
              
              <button
                type="button"
                onClick={goToNextWeek}
                className="flex items-center justify-center w-6 h-6 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                title="Sonraki Hafta"
              >
                <MdChevronRight size={16} />
              </button>
            </div>
          </div>
         </div>
         </div>
       </div>

      

             {/* Timesheet Tablosu */}
       {selectedCustomer && selectedWeek && (
         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
           <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
             <h2 className="text-lg font-medium text-gray-800 dark:text-white">
               {t('timesheet.weeklyTimesheet')}
             </h2>
           </div>
          
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">{t('timesheet.loading')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-2 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-20 sm:w-36">
                      {t('timesheet.date')}
                    </th>
                    <th className="px-1 sm:px-4 py-1 sm:py-2 text-center text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-16 sm:w-44">
                      {t('timesheet.workHour')}
                    </th>
                    <th className="px-2 sm:px-4 py-1 sm:py-2 text-left text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                      {t('timesheet.detail')}
                    </th>
                    <th className="px-1 sm:px-4 py-1 sm:py-2 text-center text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 w-12 sm:w-20">
                      {t('timesheet.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 rounded-bl-lg rounded-br-lg">
                  {weekDays.map((day, index) => (
                    <tr key={day.date} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-2 sm:px-4 py-1 sm:py-2 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                        {day.date.split('-').reverse().join('.')}
                      </td>
                      <td className="px-1 sm:px-4 py-1 sm:py-2 whitespace-nowrap text-center">
                        {editingDay === day.date ? (
                          <input
                            type="number"
                            step="1"
                            min="0"
                            max="24"
                            value={editData.workhour}
                            onChange={(e) => setEditData({ ...editData, workhour: e.target.value })}
                            placeholder="0"
                            className="w-12 sm:w-20 px-1 sm:px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded text-xs sm:text-sm text-center"
                          />
                        ) : (
                          <span className="text-xs sm:text-sm text-gray-900 dark:text-white">
                            {formatWorkHour(timesheetData[day.date]?.workhour)}
                          </span>
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-1 sm:py-2">
                        {editingDay === day.date ? (
                          <textarea
                            value={editData.detail}
                            onChange={(e) => setEditData({ ...editData, detail: e.target.value })}
                            placeholder={t('timesheet.enterWorkDetail')}
                            className="w-full px-1 sm:px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded text-xs sm:text-sm resize-none"
                            rows="2"
                          />
                        ) : (
                          <div className="text-xs sm:text-sm text-gray-900 dark:text-white text-justify">
                            {timesheetData[day.date]?.detail || '-'}
                          </div>
                        )}
                      </td>
                      <td className="px-1 sm:px-4 py-1 sm:py-2 whitespace-nowrap text-xs sm:text-sm font-medium text-center">
                        {editingDay === day.date ? (
                          <div className="flex gap-1 sm:gap-2 justify-center">
                            <button
                              onClick={() => handleSave(day)}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                            >
                              <MdSave size={16} />
                            </button>
                            <button
                              onClick={handleCancel}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <MdCancel size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-center">
                            <button
                              onClick={() => handleEdit(day)}
                              className="text-black hover:text-gray-700 dark:text-white dark:hover:text-gray-300"
                            >
                              <TbEdit size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default TimesheetPage;
